import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../../utils/api";
import VideoPlayer from "./VideoPlayer";
import RelatedVideosList from "../../components/RelatedVideosList";
import VideoComments from "../../components/comments/VideoComments";
import SubscribeButton from "../../components/SubscribeButton";
import "./videoDetails.css";

import {
  FiThumbsUp,
  FiMessageCircle,
  FiShare2,
  FiEye,
} from "react-icons/fi";

const GUEST_USER_ID = "000000000000000000000000";

const VideoDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const userId = user?._id || GUEST_USER_ID;

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  const [likeCount, setLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [shareCount, setShareCount] = useState(0);

  useEffect(() => {
    let mounted = true;

    const fetchVideo = async () => {
      try {
        const res = await api.get("/client/video/detailsOfVideo", {
          params: {
            userId,
            videoId: id,
            videoType: 1,
          },
        });

        if (mounted && res.data?.status) {
          const v = res.data.detailsOfVideo;
          setVideo(v);
          setLikeCount(v.like ?? 0);
          setIsLiked(v.isLike === true);
          setShareCount(v.shareCount ?? 0);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (id) fetchVideo();
    return () => (mounted = false);
  }, [id, userId]);

  const handleLike = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    const previousLiked = isLiked;
    setIsLiked(!previousLiked);
    setLikeCount((prev) => (previousLiked ? prev - 1 : prev + 1));

    try {
      await api.get("/client/video/likeOrDislikeOfVideo", {
        params: {
          userId: user._id,
          videoId: video._id,
          likeOrDislike: "like",
        },
      });
    } catch {
      setIsLiked(previousLiked);
      setLikeCount((prev) => (previousLiked ? prev + 1 : prev - 1));
    }
  };

  const handleCommentClick = () => {
    if (!user) {
      navigate("/login");
    } else {
      document.getElementById("comments")?.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/video/${video._id}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: video.title,
          text: "Check out this video",
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Link copied!");
      }

      setShareCount((prev) => prev + 1);

      await api.get("/client/video/shareVideo", {
        params: {
          userId,
          videoId: video._id,
        },
      });
    } catch (e) {
      console.error("Share failed", e);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!video) return <div>Video not found</div>;

  return (
    <>
      <Helmet>
        <title>{video.title} | ShareVideo</title>
        <meta
          name="description"
          content={
            video.description && video.description.length > 150
              ? video.description.slice(0, 150)
              : `Watch ${video.title} on ShareVideo. Discover engaging videos, shorts, music, audio songs and creative content from talented creators.`
          }
        />
      </Helmet>

      <div className="video-page">
        <div className="video-left">

          <div className="video-player-wrapper">
            <VideoPlayer
              src={video.videoUrl}
              userId={userId}
              videoId={video._id}
              videoUserId={video.userId}
              videoChannelId={video.channelId}
            />
          </div>

          <div className="video-content">

            <h1>{video.title}</h1>

            <p style={{ color: "#666" }}>
              <FiEye /> {(video.views ?? 0).toLocaleString()} views
            </p>

            <div
              className="channel-row"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 16,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                }}
                onClick={() =>
                  navigate(`/user/channel/${video.channelId}`)
                }
              >
                <img
                  src={video.channelImage || "/default-avatar.png"}
                  alt="channel"
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
                <div>
                  <strong>{video.channelName || "Channel"}</strong>
                </div>
              </div>

              {video.channelId && (
                <SubscribeButton
                  channelId={video.channelId}
                  initialSubscribed={video.isSubscribed}
                />
              )}
            </div>

            <div className="video-description">
              {video.description && video.description.length > 100 ? (
                <p>{video.description}</p>
              ) : (
                <p>
                  This video is available on ShareVideo. Explore videos,
                  short clips, music performances, standalone audio
                  tracks, and creative image posts from talented
                  creators across multiple categories.
                </p>
              )}
            </div>

            <div style={{ marginTop: 24 }}>
              <h3>About This Content</h3>
              <p>
                ShareVideo is a multi-format content platform supporting
                videos, vertical shorts, music uploads, standalone audio
                songs, and photo posts. Discover trending and recommended
                content while supporting independent creators.
              </p>
            </div>

            <div style={{ display: "flex", gap: 24, marginTop: 20 }}>
              <span
                onClick={handleLike}
                style={{
                  cursor: user ? "pointer" : "not-allowed",
                  opacity: user ? 1 : 0.6,
                  color: isLiked ? "#ff0050" : "#333",
                }}
              >
                <FiThumbsUp /> {likeCount}
              </span>

              <span
                onClick={handleCommentClick}
                style={{
                  cursor: user ? "pointer" : "not-allowed",
                  opacity: user ? 1 : 0.6,
                }}
              >
                <FiMessageCircle /> {video.totalComments ?? 0}
              </span>

              <span
                onClick={handleShare}
                style={{ cursor: "pointer" }}
              >
                <FiShare2 /> {shareCount}
              </span>
            </div>

            <div id="comments" style={{ marginTop: 32 }}>
              <VideoComments
                videoId={video._id}
                currentUser={user}
              />
            </div>

          </div>
        </div>

        <div className="video-right">
          <RelatedVideosList videoId={video._id} />
        </div>
      </div>
    </>
  );
};

export default VideoDetails;